import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

/**
 * StoryContext — Narrative state provider for Vertical Media Machines storytelling
 * Tracks chapter, beat, scroll progress, and enables cross-page narrative continuity
 */

const StoryContext = createContext(null);

// Chapter configuration — maps routes to narrative chapters
const CHAPTER_CONFIG = {
  '/': { id: 'home', label: 'Pilot', order: 0, color: 'memory' },
  '/ice': { id: 'memory', label: 'Chapter 1: Memory', order: 1, color: 'memory' },
  '/goingmerry': { id: 'velocity', label: 'Chapter 2: Velocity', order: 2, color: 'velocity' },
  '/ramsey': { id: 'truth', label: 'Chapter 3: Truth', order: 3, color: 'truth' },
  '/cortex': { id: 'symphony', label: 'Chapter 4: Symphony', order: 4, color: 'symphony' },
  '/contact': { id: 'credits', label: 'Credits & Next Season', order: 5, color: 'orchestration' },
  '/download': { id: 'download', label: 'Get Started', order: 6, color: 'memory' },
  '/auth': { id: 'auth', label: 'Access', order: 7, color: 'memory' },
};

// Beat thresholds — scroll percentages that trigger narrative beats
const BEAT_THRESHOLDS = {
  home: [
    { id: 'cold-open', label: 'Cold Open', threshold: 0 },
    { id: 'inciting', label: 'Inciting Incident', threshold: 0.25 },
    { id: 'proof', label: 'Proof', threshold: 0.40 },
    { id: 'capabilities', label: 'Capabilities', threshold: 0.55 },
    { id: 'ensemble', label: 'Ensemble Cast', threshold: 0.70 },
    { id: 'cta', label: 'Call to Adventure', threshold: 0.90 },
  ],
  memory: [
    { id: 'prologue', label: 'Prologue', threshold: 0 },
    { id: 'problem', label: 'The Problem', threshold: 0.20 },
    { id: 'reveal', label: 'The Reveal', threshold: 0.35 },
    { id: 'spell', label: 'The Spell', threshold: 0.50 },
    { id: 'architecture', label: 'The Architecture', threshold: 0.65 },
    { id: 'verdict', label: 'The Verdict', threshold: 0.80 },
    { id: 'epilogue', label: 'Epilogue', threshold: 0.95 },
  ],
  velocity: [
    { id: 'atmosphere', label: 'Atmosphere', threshold: 0 },
    { id: 'claim', label: 'The Claim', threshold: 0.15 },
    { id: 'evidence', label: 'The Evidence', threshold: 0.30 },
    { id: 'under-hood', label: 'Under the Hood', threshold: 0.45 },
    { id: 'ecosystem', label: 'The Ecosystem', threshold: 0.60 },
    { id: 'delight', label: 'Delight', threshold: 0.75 },
    { id: 'decision', label: 'Decision', threshold: 0.90 },
  ],
  truth: [
    { id: 'gravity', label: 'Gravity', threshold: 0 },
    { id: 'pillars', label: 'Three Pillars', threshold: 0.20 },
    { id: 'demo', label: 'The Demo', threshold: 0.35 },
    { id: 'shield', label: 'The Shield', threshold: 0.50 },
    { id: 'faq', label: 'The FAQ', threshold: 0.65 },
    { id: 'quote', label: 'The Quote', threshold: 0.85 },
  ],
  symphony: [
    { id: 'overture', label: 'Overture', threshold: 0 },
    { id: 'movements', label: 'Movements', threshold: 0.25 },
    { id: 'conductor', label: 'The Conductor', threshold: 0.50 },
    { id: 'finale', label: 'Finale', threshold: 0.75 },
  ],
  credits: [
    { id: 'title-card', label: 'Title Card', threshold: 0 },
    { id: 'manifesto', label: 'Manifesto', threshold: 0.15 },
    { id: 'cast', label: 'The Cast', threshold: 0.30 },
    { id: 'values', label: 'The Values', threshold: 0.45 },
    { id: 'network', label: 'The Network', threshold: 0.60 },
    { id: 'face', label: 'The Face', threshold: 0.75 },
    { id: 'badge', label: 'The Badge', threshold: 0.85 },
    { id: 'door', label: 'The Door', threshold: 0.95 },
  ],
  download: [
    { id: 'landing', label: 'Landing', threshold: 0 },
    { id: 'options', label: 'Options', threshold: 0.50 },
  ],
  auth: [
    { id: 'gate', label: 'Gate', threshold: 0 },
  ],
};

export const StoryProvider = ({ children }) => {
  const [currentPath] = useState(typeof window !== 'undefined' ? window.location.pathname : '/');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentBeat, setCurrentBeat] = useState(null);
  const [beatHistory, setBeatHistory] = useState([]);
  const [chapterProgress, setChapterProgress] = useState({});
  const [reducedMotion, setReducedMotion] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  // Memoized chapter info
  const chapter = useMemo(() => CHAPTER_CONFIG[currentPath] || CHAPTER_CONFIG['/'], [currentPath]);
  const beats = useMemo(() => BEAT_THRESHOLDS[chapter.id] || [], [chapter.id]);

  // Detect reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);

    const handler = (e) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Track scroll progress and beat transitions
  useEffect(() => {
    let scrollTimeout;

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? scrollTop / docHeight : 0;

      setScrollProgress(progress);
      setIsScrolling(true);

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => setIsScrolling(false), 150);

      // Determine current beat based on scroll position
      const currentBeatData = beats
        .slice()
        .reverse()
        .find((beat) => progress >= beat.threshold);

      if (currentBeatData && currentBeatData.id !== currentBeat?.id) {
        setCurrentBeat(currentBeatData);
        setBeatHistory((prev) => {
          // Keep last 10 beats, avoid duplicates
          const filtered = prev.filter((b) => b.id !== currentBeatData.id);
          return [...filtered.slice(-9), { ...currentBeatData, timestamp: Date.now() }];
        });
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [beats, currentBeat]);

  // Track chapter completion progress
  useEffect(() => {
    const finalBeat = beats[beats.length - 1];
    if (finalBeat && scrollProgress >= finalBeat.threshold) {
      setChapterProgress((prev) => ({
        ...prev,
        [chapter.id]: { completed: true, completedAt: Date.now() },
      }));
    }
  }, [scrollProgress, chapter.id, beats]);

  // Navigation helpers
  const navigateToChapter = useCallback((chapterId) => {
    const entry = Object.entries(CHAPTER_CONFIG).find(([, v]) => v.id === chapterId);
    if (entry) {
      window.location.href = entry[0];
    }
  }, []);

  const getNextChapter = useCallback(() => {
    const currentOrder = chapter.order;
    const nextEntry = Object.entries(CHAPTER_CONFIG).find(([, v]) => v.order === currentOrder + 1);
    return nextEntry ? { path: nextEntry[0], ...nextEntry[1] } : null;
  }, [chapter.order]);

  const getPreviousChapter = useCallback(() => {
    const currentOrder = chapter.order;
    const prevEntry = Object.entries(CHAPTER_CONFIG).find(([, v]) => v.order === currentOrder - 1);
    return prevEntry ? { path: prevEntry[0], ...prevEntry[1] } : null;
  }, [chapter.order]);

  // Cross-page narrative continuity — detect where user came from
  const [referrerChapter, setReferrerChapter] = useState(null);

  useEffect(() => {
    // Check if we have a stored referrer from sessionStorage
    const stored = sessionStorage.getItem('dopove_last_chapter');
    if (stored && stored !== chapter.id) {
      setReferrerChapter(stored);
    }
    // Update stored chapter
    sessionStorage.setItem('dopove_last_chapter', chapter.id);
  }, [chapter.id]);

  // Chapter progress for all chapters (for progress indicator)
  const allChapterProgress = useMemo(() => {
    return Object.values(CHAPTER_CONFIG)
      .sort((a, b) => a.order - b.order)
      .map((ch) => ({
        ...ch,
        progress: chapterProgress[ch.id]?.completed ? 1 : (ch.id === chapter.id ? scrollProgress : 0),
        isCurrent: ch.id === chapter.id,
        isCompleted: chapterProgress[ch.id]?.completed || false,
      }));
  }, [chapterProgress, chapter.id, scrollProgress]);

  const value = useMemo(() => ({
    // Current state
    chapter,
    chapterId: chapter.id,
    chapterLabel: chapter.label,
    chapterColor: chapter.color,
    scrollProgress,
    currentBeat,
    beatHistory,
    isScrolling,
    reducedMotion,

    // Chapter navigation
    allChapters: Object.values(CHAPTER_CONFIG).sort((a, b) => a.order - b.order),
    allChapterProgress,
    nextChapter: getNextChapter(),
    previousChapter: getPreviousChapter(),
    navigateToChapter,

    // Cross-page continuity
    referrerChapter,
    hasReferrer: !!referrerChapter,

    // Beat utilities
    beats,
    isBeatActive: (beatId) => currentBeat?.id === beatId,
    isBeatCompleted: (beatId) => beatHistory.some((b) => b.id === beatId),
  }), [
    chapter,
    scrollProgress,
    currentBeat,
    beatHistory,
    isScrolling,
    reducedMotion,
    allChapterProgress,
    getNextChapter,
    getPreviousChapter,
    navigateToChapter,
    referrerChapter,
    beats,
  ]);

  return (
    <StoryContext.Provider value={value}>
      {/* Apply chapter color to document for CSS variable access */}
      <div data-chapter={chapter.color} style={{ display: 'contents' }}>
        {children}
      </div>
    </StoryContext.Provider>
  );
};

export const useStory = () => {
  const context = useContext(StoryContext);
  if (!context) {
    throw new Error('useStory must be used within a StoryProvider');
  }
  return context;
};

// Utility hook for scroll-triggered animations
export const useScrollReveal = (threshold = 0.1, rootMargin = '0px') => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = React.useRef(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [elementRef, isVisible];
};

// Utility hook for stagger delays
export const useStaggerDelay = (index, baseDelay = 150) => {
  return index * baseDelay;
};

export default StoryContext;